import { lazy, Suspense, useEffect } from "react";
import { initGA, grantAnalyticsConsent, hasAnalyticsConsent } from "@/lib/analytics";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, useSearchParams, Navigate } from "react-router-dom";
import { ScrollToTop } from "@/components/ScrollToTop";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { ChatWidgetProvider } from "@/hooks/useChatWidget";
import { PhotoGateProvider } from "@/hooks/usePhotoGate";
import { FloatingChatWidget } from "@/components/FloatingChatWidget";
import { PushNotificationPrompt } from "@/components/PushNotificationPrompt";
import { CookieConsent } from "@/components/CookieConsent";
import { EmailConfirmationToast } from "@/components/EmailConfirmationToast";
import { WhatsAppBanner } from "@/components/WhatsAppBanner";
import { BottomNavigation } from "@/components/BottomNavigation";
import { FeedbackWidget } from "@/components/FeedbackWidget";
import { Navigation } from "@/components/Navigation";

import { NotificationPopup } from "@/components/NotificationPopup";
import { OnboardingTourGate } from "@/components/onboarding/OnboardingTourGate";
import { RouteTracker } from "@/components/RouteTracker";
import { useDraftReminder } from "@/hooks/useDraftReminder";
import { useVersionCheck } from "@/hooks/useVersionCheck";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { isMedianApp } from "@/lib/medianOneSignal";
import { isFocusedView } from "@/lib/focusedViewRoutes";
import { useWorkspaceMode } from "@/hooks/useWorkspaceMode";
import { BusinessNavigation } from "@/components/business/BusinessNavigation";
import { BusinessBottomNavigation } from "@/components/business/BusinessBottomNavigation";

// Homepage stays eager (LCP route). Everything else is code-split.
import Index from "./pages/Index";
import { ProfileSubNav } from "@/components/ProfileSubNav";

// Wrap dynamic imports so a stale chunk (after a new deploy) triggers one
// automatic hard reload instead of a blank screen.
const lazyWithReload = <T,>(factory: () => Promise<T>) =>
  lazy(() =>
    (factory() as Promise<any>)
      // First failure is usually a transient dev/HMR or CDN hiccup: retry once.
      .catch(() => new Promise((r) => setTimeout(r, 400)).then(() => factory() as Promise<any>))
      .then((mod) => {
        sessionStorage.removeItem("lovable:chunk-reload");
        return mod;
      })
      .catch((err) => {
        const key = "lovable:chunk-reload";
        if (typeof window !== "undefined" && !sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, "1");
          window.location.reload();
          return new Promise(() => {}); // never resolves; page is reloading
        }
        throw err;
      })
  ) as ReturnType<typeof lazy>;


const About = lazyWithReload(() => import("./pages/About"));
const Admin = lazyWithReload(() => import("./pages/Admin"));
const MemberPerks = lazyWithReload(() => import("./pages/MemberPerks"));
const Auth = lazyWithReload(() => import("./pages/Auth"));
const Contact = lazyWithReload(() => import("./pages/Contact"));
const HelpSupport = lazyWithReload(() => import("./pages/HelpSupport"));
const DeleteAccount = lazyWithReload(() => import("./pages/DeleteAccount"));
const CommunityRules = lazyWithReload(() => import("./pages/CommunityRules"));
const Interested = lazyWithReload(() => import("./pages/Interested"));
const Activity = lazyWithReload(() => import("./pages/Activity"));
const Users = lazyWithReload(() => import("./pages/Users"));
const Community = lazyWithReload(() => import("./pages/Community"));
const CirclePage = lazyWithReload(() => import("./pages/Circle"));
const CircleMembersPage = lazyWithReload(() => import("./pages/CircleMembers"));
const UserProfile = lazyWithReload(() => import("./pages/UserProfile"));

const PrivacyPolicy = lazyWithReload(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazyWithReload(() => import("./pages/TermsOfService"));
const CookiePolicy = lazyWithReload(() => import("./pages/CookiePolicy"));
const ResetPassword = lazyWithReload(() => import("./pages/ResetPassword"));
const NotFound = lazyWithReload(() => import("./pages/NotFound"));
const Advertise = lazyWithReload(() => import("./pages/Advertise"));
const PaymentSuccess = lazyWithReload(() => import("./pages/PaymentSuccess"));
const BookingSuccess = lazyWithReload(() => import("./pages/BookingSuccess"));
const BookingCancel = lazyWithReload(() => import("./pages/BookingCancel"));
const FoxAndCubs = lazyWithReload(() => import("./pages/FoxAndCubs"));
const HalloweenAtMowglis = lazyWithReload(() => import("./pages/HalloweenAtMowglis"));
const PlayDatePage = lazyWithReload(() => import("./pages/PlayDate"));
const CreatePlayDate = lazyWithReload(() => import("./pages/CreatePlayDate"));
const MyPlayDates = lazyWithReload(() => import("./pages/MyPlayDates"));
const MyBookings = lazyWithReload(() => import("./pages/MyBookings"));
const Messages = lazyWithReload(() => import("./pages/Messages"));
const Conversation = lazyWithReload(() => import("./pages/Conversation"));
const Noticeboard = lazyWithReload(() => import("./pages/Noticeboard"));
const NoticeboardPost = lazyWithReload(() => import("./pages/NoticeboardPost"));
const MyNoticeboard = lazyWithReload(() => import("./pages/MyNoticeboard"));
const Stories = lazyWithReload(() => import("./pages/Stories"));
const Activities = lazyWithReload(() => import("./pages/Activities"));
const CategoryMap = lazyWithReload(() => import("./pages/CategoryMap"));
const AreaHub = lazyWithReload(() => import("./pages/AreaHub"));
const MeetLocalParents = lazyWithReload(() => import("./pages/MeetLocalParents"));
const Settings = lazyWithReload(() => import("./pages/Settings"));
const Business = lazyWithReload(() => import("./pages/Business"));


const DraftReminderCheck = () => { useDraftReminder(); return null; };
const VersionCheck = () => {
  const { isAdmin, loading } = useIsAdmin();
  useVersionCheck(!loading, isAdmin);
  return null;
};

const PushNotificationPromptGate = () => {
  const location = useLocation();
  if (location.pathname === "/admin") return null;
  return <PushNotificationPrompt />;
};

const AnalyticsBootstrap = () => {
  useEffect(() => {
    // Basic consent mode: GA loads only for users who previously accepted.
    if (!hasAnalyticsConsent()) return;
    const run = () => initGA();
    const w = window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number };
    if (typeof w.requestIdleCallback === "function") {
      w.requestIdleCallback(run, { timeout: 4000 });
    } else {
      setTimeout(run, 2500);
    }
  }, []);
  return null;
};

/** Hides BottomNavigation + FloatingChatWidget on /admin */
const GlobalChrome = () => {
  const location = useLocation();
  const { user } = useAuth();
  if (location.pathname === "/admin" || location.pathname.startsWith("/messages/")) return null;
  if (location.pathname.startsWith("/business")) return <BusinessBottomNavigation />;
  // Activity details and other people's profiles are focused views: the bottom pill
  // is hidden there so the content gets the full height. Both carry their own
  // floating back arrow; the top bar still carries the menu, create button and avatar.
  const hideBottomNav = isFocusedView(location.pathname, user?.id);
  return (
    <>
      {!hideBottomNav && <BottomNavigation />}
      <FloatingChatWidget />
    </>
  );
};

/** Business owners (business-only or dual-role) land in the business workspace once per session, unless they chose Member view. */
const BusinessLandingRedirect = () => {
  const location = useLocation();
  const { user } = useAuth();
  const { canUseBusiness, mode, profileLoaded } = useWorkspaceMode();
  const { isAdmin, loading: adminLoading } = useIsAdmin();
  if (!user || !profileLoaded || !canUseBusiness) return null;
  // The old member-side "My Activities" page: always send non-admin business owners to their
  // workspace (whatever view they're in), carrying intent like "open the submit form".
  if (location.pathname === "/admin" && !adminLoading && !isAdmin) {
    return <Navigate to={`/business${location.search}`} state={location.state} replace />;
  }
  if (mode !== "business") return null;
  if (location.pathname !== "/" && location.pathname !== "/activities") return null;
  const flag = `bb:business-landed:${user.id}`;
  try {
    if (sessionStorage.getItem(flag)) return null;
    sessionStorage.setItem(flag, "1");
  } catch { return null; }
  return <Navigate to="/business" replace />;
};

/** Persistent header. Hidden on mobile for detail pages that use a floating back arrow. */
const GlobalHeader = () => {
  const location = useLocation();
  const { user } = useAuth();
  const { isAdmin, loading: adminLoading } = useIsAdmin();
  const path = location.pathname;
  if (path.startsWith("/business")) return <BusinessNavigation />;
  // Admin panel has its own header — hide the visitor nav only for real admins.
  if (path === "/admin") {
    if (adminLoading || isAdmin) return null;
    return <Navigation />;
  }

  // 1:1 chat is a full-height view with its own header + sticky composer.
  if (path.startsWith("/messages/")) return null;

  // Keep the nav visible on the current user's own profile page ("My profile").
  const isOwnProfile = !!user && path === `/user/${user.id}`;
  const hideOnMobile =
    !isOwnProfile && (
      path.startsWith("/activity/") ||
      path.startsWith("/play-date/") ||
      path.startsWith("/user/")
    );
  if (hideOnMobile) {
    return <div className="hidden md:block"><Navigation /></div>;
  }
  return <Navigation />;
};


const UsersRedirect = () => {
  const [searchParams] = useSearchParams();
  return <Navigate to={{ pathname: '/playdates', search: searchParams.toString() }} replace />;
};

/**
 * The homepage is a marketing page: inside the Median native app it is skipped
 * entirely so the app always opens on Activities. Unchanged on the web/PWA.
 */
const HomeRoute = () => {
  if (isMedianApp()) return <Navigate to="/activities" replace />;
  return <Index />;
};

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>
);

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <ChatWidgetProvider>
        <PhotoGateProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
              <PushNotificationPromptGate />
            <RouteTracker />
            <EmailConfirmationToast />
            <WhatsAppBanner />
            <CookieConsent />
            <NotificationPopup />
            <DraftReminderCheck />
            <VersionCheck />
            <AnalyticsBootstrap />
            <OnboardingTourGate />
            <BusinessLandingRedirect />
            <GlobalHeader />
            <Suspense fallback={<RouteFallback />}>
              <main id="main-content">
                <Routes>
                  <Route path="/" element={<HomeRoute />} />
                  <Route path="/activities" element={<Activities />} />
                  <Route path="/activities/category/:slug" element={<CategoryMap />} />
                  <Route path="/activities/:area" element={<AreaHub />} />
                  <Route path="/activities/:area/:category" element={<AreaHub />} />
                  <Route path="/about" element={<Navigate to="/cecile-serena-story" replace />} />
                  <Route path="/cecile-serena-story" element={<About />} />
                  <Route path="/admin" element={<Admin />} />
                  <Route path="/member-perks" element={<MemberPerks />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/help" element={<HelpSupport />} />
                  <Route path="/delete-account" element={<DeleteAccount />} />
                <Route path="/community-rules" element={<CommunityRules />} />
                <Route path="/meet-local-parents-balham" element={<MeetLocalParents />} />
                  <Route path="/interested" element={<Interested />} />
                  <Route path="/my-noticeboard" element={<MyNoticeboard />} />
                  <Route path="/activity/:id" element={<Activity />} />
                  <Route path="/playdates" element={<Users />} />
                  <Route path="/community" element={<Community />} />
                  <Route path="/community/circles/:slug" element={<CirclePage />} />
                  <Route path="/community/circles/:slug/members" element={<CircleMembersPage />} />
                  <Route path="/users" element={<UsersRedirect />} />
                  <Route path="/user/:id" element={<UserProfile />} />
                  
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/terms-of-service" element={<TermsOfService />} />
                  <Route path="/cookie-policy" element={<CookiePolicy />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/advertise" element={<Advertise />} />
                  <Route path="/payment-success" element={<PaymentSuccess />} />
                  <Route path="/booking-success" element={<BookingSuccess />} />
                  <Route path="/booking-cancel" element={<BookingCancel />} />
                  <Route path="/partners/fox-and-cubs" element={<FoxAndCubs />} />
                  <Route path="/fox-and-cubs" element={<Navigate to="/partners/fox-and-cubs" replace />} />
                  <Route path="/halloween-at-mowglishome" element={<HalloweenAtMowglis />} />
                  <Route path="/messages" element={<Messages />} />
                  <Route path="/messages/:id" element={<Conversation />} />
                  <Route path="/noticeboard" element={<Noticeboard />} />
                  <Route path="/noticeboard/:id" element={<NoticeboardPost />} />
                  <Route path="/stories" element={<Navigate to="/local-spotlights" replace />} />
                  <Route path="/local-spotlights" element={<Stories />} />
                  <Route path="/play-date/:id" element={<PlayDatePage />} />
                  <Route path="/create-play-date" element={<CreatePlayDate />} />
                  <Route path="/my-play-dates" element={<MyPlayDates />} />
                  <Route path="/my-bookings" element={<MyBookings />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/business" element={<Business />} />
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </main>
            </Suspense>
            <GlobalChrome />
            <FeedbackWidget />
          </BrowserRouter>
        </TooltipProvider>
      </PhotoGateProvider>
      </ChatWidgetProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
